import axios from 'axios';
import { toast } from 'react-toastify';
import { authorize } from '../shared/authorize';
import { apiAdmin } from './api';

/**
 * Create a Stripe Checkout Session for Klarna.
 * Expected backend payload: { course_id, course, amount (cents), currency: "eur" }
 * Expected response: { status: true, data: { session_id, client_secret }, action: "Checkout session created" }
 */
export const createKlarnaCheckoutSession = async ({
  courseId,
  amount,
  title,
}) => {
  try {
    const body = {
      course_id: courseId,
      course: title,
      amount,
      currency: 'eur',
    };

    const response = await apiAdmin.post(
      'user/payment/stripe/payment-session',
      body,
      authorize()
    );
    return response.data;
  } catch (error) {
    console.error('Error creating Klarna checkout session:', error);
    toast.error(error?.response?.data?.message || 'Error starting Klarna checkout');
    throw error;
  }
};

/**
 * Retrieve the status of a Stripe Checkout Session from the backend.
 * Endpoint: GET user/payment/stripe/payment-status/{sessionId}
 * Expected response: { status: true, data: { status, payment_status } }
 */
export const getKlarnaCheckoutSessionStatus = async (sessionId) => {
  try {
    const response = await apiAdmin.get(
      `user/payment/stripe/payment-status/${sessionId}`,
      authorize()
    );
    return response.data;
  } catch (error) {
    console.error('Error retrieving Klarna checkout session status:', error);
    toast.error(error?.response?.data?.message || 'Error verifying payment status');
    throw error;
  }
};

// Mock helpers kept below for backward compatibility / other Stripe flows.
// These are NOT used for the Klarna checkout flow above.

export const createStripePaymentIntent = async (amount, courseId) => {
  try {
    const mockResponse = {
      status: true,
      data: {
        clientSecret: `pi_test_${Date.now()}_secret_${Math.random().toString(36).substr(2, 9)}`,
        paymentIntentId: `pi_test_${Date.now()}`,
        amount: amount * 100,
        currency: 'eur',
        status: 'requires_payment_method',
      },
    };
    await new Promise((resolve) => setTimeout(resolve, 800));
    console.log('Mock Payment Intent Created:', mockResponse.data);
    return mockResponse;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    toast.error('Error processing payment');
    throw error;
  }
};

export const confirmStripePayment = async (paymentIntentId, paymentMethodId, amount, courseId) => {
  try {
    const mockResponse = {
      status: true,
      data: {
        paymentIntentId,
        paymentMethodId,
        status: 'succeeded',
        amount,
        courseId,
        transactionId: `txn_${Date.now()}`,
        message: 'Payment processed successfully',
      },
    };
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log('Mock Payment Confirmed:', mockResponse.data);
    return mockResponse;
  } catch (error) {
    console.error('Error confirming payment:', error);
    toast.error('Error confirming payment');
    throw error;
  }
};

export const saveStripePaymentMethod = async (paymentMethodId, email, cardholderName) => {
  try {
    const mockResponse = {
      status: true,
      data: {
        paymentMethodId,
        email,
        cardholderName,
        last4: '4242',
        brand: 'visa',
        savedAt: new Date().toISOString(),
        message: 'Payment method saved successfully',
      },
    };
    await new Promise((resolve) => setTimeout(resolve, 600));
    console.log('Mock Payment Method Saved:', mockResponse.data);
    return mockResponse;
  } catch (error) {
    console.error('Error saving payment method:', error);
    toast.error('Error saving payment method');
    throw error;
  }
};

export const getStripePaymentMethods = async () => {
  try {
    const mockResponse = {
      status: true,
      data: [
        {
          paymentMethodId: 'pm_mock_1234',
          last4: '4242',
          brand: 'visa',
          expMonth: 12,
          expYear: 2025,
          email: 'user@example.com',
        },
      ],
    };
    await new Promise((resolve) => setTimeout(resolve, 500));
    console.log('Mock Payment Methods Retrieved:', mockResponse.data);
    return mockResponse;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    toast.error('Error fetching payment methods');
    throw error;
  }
};

export const deleteStripePaymentMethod = async (paymentMethodId) => {
  try {
    const mockResponse = {
      status: true,
      data: {
        message: 'Payment method deleted successfully',
        deletedPaymentMethodId: paymentMethodId,
      },
    };
    await new Promise((resolve) => setTimeout(resolve, 500));
    console.log('Mock Payment Method Deleted:', mockResponse.data);
    return mockResponse;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    toast.error('Error deleting payment method');
    throw error;
  }
};

export const purchaseCourseWithStripe = async (courseId, paymentMethodId, amount) => {
  try {
    const mockResponse = {
      status: true,
      data: {
        courseId,
        transactionId: `txn_${Date.now()}`,
        paymentStatus: 'completed',
        amount,
        purchaseDate: new Date().toISOString(),
        message: 'Course purchased successfully',
        courseAccessLink: `/courses/${courseId}`,
      },
    };
    await new Promise((resolve) => setTimeout(resolve, 1200));
    console.log('Mock Course Purchase Success:', mockResponse.data);
    toast.success('Course purchased successfully!');
    return mockResponse;
  } catch (error) {
    console.error('Error purchasing course:', error);
    toast.error('Error purchasing course');
    throw error;
  }
};
